import { forwardRef, type ButtonHTMLAttributes } from 'react';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required for accessibility. Describes the button action for screen readers. */
  'aria-label': string;
  /** sm = 32px, md = 44px (WCAG compliant), lg = 48px. Default: md */
  size?: 'sm' | 'md' | 'lg';
  /** ghost = transparent bg with hover, muted = subtle text color. Default: ghost */
  variant?: 'ghost' | 'muted';
}

const sizeClasses = {
  sm: 'min-w-[32px] min-h-[32px]',
  md: 'min-w-[44px] min-h-[44px]',
  lg: 'min-w-[48px] min-h-[48px]',
} as const;

const variantClasses = {
  ghost: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700',
  muted: 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300',
} as const;

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'md', variant = 'ghost', className = '', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={`
          flex items-center justify-center rounded-lg transition
          focus:outline-none focus:ring-2 focus:ring-green-500
          disabled:opacity-50 disabled:cursor-not-allowed
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          ${className}
        `.trim()}
        {...props}
      >
        {children}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
export default IconButton;
